import React, { useState } from "react"
import * as XLSX from "xlsx"

import { db, arrayUnion } from "../../utils/firebase"

// UI components
import Button from "../../components/ui/button/button"

/** Client documents fetched per round while checking candidates. */
const READ_CHUNK_SIZE = 50

/** Firestore caps a batched write at 500 operations. */
const WRITE_BATCH_SIZE = 450

/** How many pending writes the dry run lists so they can be eyeballed. */
const PREVIEW_ROWS = 25

const text = (value) => String(value ?? "").trim()

/**
 * Read Golfbreaks' email alias export (xlsx or csv) and keep the rows that need
 * an alias: a Firebase client id whose processed key (`emailKey`) differs from
 * the raw address we hold (`emailRaw`). Rows flagged as test data are dropped.
 */
const readCandidates = async (file) => {
	const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false, cellNF: false, cellText: false })
	const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: "" })

	const tally = { rows: rows.length, test: 0, unusable: 0, same: 0 }
	const candidates = []

	rows.forEach((row) => {
		const clientID = text(row.firebaseId)
		const emailRaw = text(row.emailRaw)
		const emailKey = text(row.emailKey)

		if (String(row.isTest) === "true") {
			tally.test += 1
		} else if (!clientID || !emailKey.includes("@")) {
			tally.unusable += 1
		} else if (emailKey === emailRaw) {
			tally.same += 1
		} else {
			candidates.push({ clientID, emailRaw, alias: emailKey.toLowerCase() })
		}
	})

	return { tally, candidates }
}

/**
 * One-off: stamp `email_aliases` onto client records from Golfbreaks' email
 * alias export, so the Stableford partner lookup finds a client by the
 * cleaned-up address Salesforce is keyed on ("name@gmail.com") when our record
 * holds the typo ("name@gmail.con") or a mixed-case spelling.
 *
 * Every candidate is checked against the live client document before anything
 * is written: the stored email must still equal the export's `emailRaw`,
 * otherwise the row is reported and skipped. Runs in the browser as the
 * logged-in staff user, so it needs no service-account key. Dry run by
 * default; apply writes the aliases with arrayUnion, so re-running is safe.
 */
export default function EmailAliasesBackfill() {
	const [fileName, setFileName] = useState("")
	const [fileTally, setFileTally] = useState(null)
	const [candidates, setCandidates] = useState([])
	const [loading, setLoading] = useState(false)
	const [report, setReport] = useState("")

	const loadFile = async (event) => {
		const file = event.target.files?.[0]
		if (!file) return

		setLoading(true)
		setCandidates([])
		setFileTally(null)
		setFileName(file.name)
		setReport(`Reading ${file.name}...`)

		try {
			const { tally, candidates: found } = await readCandidates(file)
			setFileTally(tally)
			setCandidates(found)
			setReport(
				[
					`File: ${file.name} (${tally.rows.toLocaleString("en-GB")} rows)`,
					`skipped - test data ........... ${tally.test}`,
					`skipped - no id or key ........ ${tally.unusable}`,
					`skipped - key equals stored ... ${tally.same}`,
					`candidates needing an alias ... ${found.length}`,
					"",
					"Run the dry run to check each candidate against its client record.",
				].join("\n")
			)
		} catch (error) {
			console.error("Alias file could not be read:", error)
			setReport(`Alias file could not be read: ${error.message || error}`)
		} finally {
			setLoading(false)
		}
	}

	const run = async (apply) => {
		setLoading(true)
		setReport("Checking client records...")

		try {
			const tally = { aliased: 0, corrected: 0, missing: 0, mismatch: 0, pending: 0, written: 0 }
			const problems = []
			const writes = []

			for (let index = 0; index < candidates.length; index += READ_CHUNK_SIZE) {
				const chunk = candidates.slice(index, index + READ_CHUNK_SIZE)
				const snapshots = await Promise.all(chunk.map((candidate) => db.doc(`clients/${candidate.clientID}`).get()))

				snapshots.forEach((snapshot, position) => {
					const { clientID, emailRaw, alias } = chunk[position]

					if (!snapshot.exists) {
						tally.missing += 1
						problems.push(`missing   ${clientID}  export=${emailRaw}`)
						return
					}

					const data = snapshot.data() || {}
					const stored = text(data.email)
					const aliases = Array.isArray(data.email_aliases) ? data.email_aliases : []

					if (aliases.includes(alias)) {
						tally.aliased += 1
					} else if (stored === alias) {
						// The record has been corrected since the export, so the exact-match lookup already finds it
						tally.corrected += 1
					} else if (stored !== emailRaw) {
						tally.mismatch += 1
						problems.push(`mismatch  ${clientID}  export=${emailRaw}  stored=${stored || "(blank)"}`)
					} else {
						tally.pending += 1
						writes.push({ ref: snapshot.ref, clientID, stored, alias })
					}
				})

				setReport(`Checked ${Math.min(index + READ_CHUNK_SIZE, candidates.length)} of ${candidates.length} candidates...`)
			}

			if (apply) {
				for (let index = 0; index < writes.length; index += WRITE_BATCH_SIZE) {
					const batch = db.batch()
					const slice = writes.slice(index, index + WRITE_BATCH_SIZE)
					slice.forEach((write) => batch.update(write.ref, { email_aliases: arrayUnion(write.alias) }))
					await batch.commit()
					tally.written += slice.length
					setReport(`Written ${tally.written} of ${writes.length} aliases...`)
				}
			}

			const lines = [
				`Mode: ${apply ? "APPLY" : "DRY RUN (no writes)"}`,
				`File: ${fileName} - ${candidates.length} candidates`,
				"",
				`already aliased ........... ${tally.aliased}`,
				`record since corrected .... ${tally.corrected}`,
				`client missing ............ ${tally.missing}`,
				`stored email mismatch ..... ${tally.mismatch}`,
				`aliases to write .......... ${tally.pending}`,
				"",
				`written ................... ${tally.written}${apply ? "" : "  (dry run)"}`,
			]

			if (!apply && writes.length) {
				lines.push("", `Preview (first ${Math.min(PREVIEW_ROWS, writes.length)} of ${writes.length}):`)
				writes.slice(0, PREVIEW_ROWS).forEach((write) => lines.push(`  ${write.clientID}  ${write.stored}  ->  ${write.alias}`))
			}

			if (problems.length) {
				lines.push("", `Skipped (${problems.length}):`)
				problems.forEach((problem) => lines.push(`  ${problem}`))
			}

			const output = lines.join("\n")
			console.log(output)
			setReport(output)
		} catch (error) {
			console.error("Email alias backfill failed:", error)
			setReport(`Email alias backfill failed: ${error.message || error}`)
		} finally {
			setLoading(false)
		}
	}

	return (
		<div style={{ marginTop: "24px" }}>
			<small style={{ display: "block", marginBottom: "8px", fontSize: "12px", opacity: "0.75" }}>
				Email aliases for the Stableford partner lookup - upload Golfbreaks' alias export (firebaseId / emailRaw / emailKey), dry run, then apply.
			</small>

			<input
				type="file"
				accept=".xlsx,.xls,.csv"
				disabled={loading}
				onChange={loadFile}
			/>

			<Button
				label="Email aliases - Dry run"
				onClick={() => run(false)}
				loading={loading}
				loadingText="Working..."
				disabled={!candidates.length}
				style={{ marginTop: "10px" }}
			/>

			<Button
				label={`Email aliases - Apply${fileTally ? ` (${candidates.length} candidates)` : ""}`}
				onClick={() => {
					if (window.confirm("Write email aliases onto the client records that pass the checks? Run the dry run first.")) run(true)
				}}
				loading={loading}
				loadingText="Working..."
				disabled={!candidates.length}
				style={{ marginTop: "10px" }}
			/>

			{report && (
				<pre style={{ marginTop: "12px", padding: "12px", background: "#f7f7f7", border: "1px solid rgba(0, 0, 0, 0.1)", borderRadius: "4px", fontSize: "12px", lineHeight: "1.5", whiteSpace: "pre-wrap", fontFamily: "monospace", overflowX: "auto" }}>
					{report}
				</pre>
			)}
		</div>
	)
}
